import dayjs from "dayjs";

/**
 * Presentation helpers shared by the Schedules tab, the schedule detail and the
 * Devices tab, so all three describe a schedule the same way.
 */

export function formatDateTime(value) {
  if (!value) return "—";
  const d = dayjs(value);
  return d.isValid() ? d.format("MMM D, YYYY · h:mm A") : "—";
}

export function formatDate(value) {
  if (!value) return "—";
  const d = dayjs(value);
  return d.isValid() ? d.format("MMM D, YYYY") : "—";
}

/**
 * The human description of when a schedule fires.
 *
 * ⚠️ `offsetMinutes` is type-specific on the backend: it is MINUTES for
 * candleLighting/motzei, DEGREES for the *Degree zman types, and ignored
 * entirely for everything else. Labelling degrees as minutes would misreport
 * the fire time, so the unit is chosen from the zman type here too.
 */
const DEGREE_TYPE = /Degree$/;

export function formatZman(zmanim) {
  if (!zmanim?.type) return null;

  const label = zmanim.type
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

  const offset = zmanim.offsetMinutes ?? 0;
  if (!offset) return label;

  if (DEGREE_TYPE.test(zmanim.type)) return `${label} (${offset}°)`;

  const abs = Math.abs(offset);
  return `${abs} min ${offset < 0 ? "before" : "after"} ${label}`;
}

export function formatWhen(schedule) {
  const when = schedule?.when;
  if (!when) return "—";

  const type = when.scheduleType || schedule.scheduleType;
  const parts = [];

  if (type) parts.push(type.charAt(0).toUpperCase() + type.slice(1));

  const zman = formatZman(when.zmanim);
  if (zman) parts.push(zman);
  else if (when.exactTime) parts.push(`at ${when.exactTime}`);

  if (when.calenderType) parts.push(`(${when.calenderType})`);

  return parts.length ? parts.join(" · ") : "—";
}

/**
 * Pause has three distinct shapes and they mean different things — a one-off
 * skip is not the same as being switched off indefinitely. Report which.
 */
export function formatPause(pause) {
  if (!pause) return null;

  if (pause.type === "forever") return "Paused indefinitely";

  if (pause.from && pause.to) {
    return `Paused ${formatDate(pause.from)} – ${formatDate(pause.to)}`;
  }
  if (pause.until) return `Paused until ${formatDate(pause.until)}`;

  if (Array.isArray(pause.on) && pause.on.length) {
    return pause.on.length === 1
      ? `Skipping ${formatDate(pause.on[0])}`
      : `Skipping ${pause.on.length} dates`;
  }

  return null;
}

/**
 * "5 devices total · 4× ON 1× OFF 2× —"
 *
 * ⚠️ `actions[].deviceOn` is nullable and null means "leave this device
 * unchanged", NOT off. It is counted separately as "—"; folding it into the OFF
 * count would misstate what the schedule is about to do.
 */
export function summariseActions(actions = []) {
  let on = 0;
  let off = 0;
  let unknown = 0;

  for (const a of actions) {
    if (a.deviceOn === true) on++;
    else if (a.deviceOn === false) off++;
    else unknown++;
  }

  const bits = [];
  if (on) bits.push(`${on}× ON`);
  if (off) bits.push(`${off}× OFF`);
  if (unknown) bits.push(`${unknown}× —`);

  const total = actions.length;
  return {
    total,
    label: `${total} device${total === 1 ? "" : "s"} total${bits.length ? ` · ${bits.join("  ")}` : ""}`,
  };
}

/**
 * Render a value, falling back to an em dash when it is genuinely absent.
 *
 * ⚠️ Only null/undefined/"" become "—". `false` and `0` are real answers and
 * must survive — printing "—" for `roundtoclosest: false` would hide the fact
 * that the field is set.
 */
export function dash(value) {
  if (value === null || value === undefined || value === "") return "—";
  return value;
}

/** A boolean that may be unset: true → Yes, false → No, absent → "—". */
export function formatBool(value) {
  if (value === null || value === undefined) return "—";
  return value ? "Yes" : "No";
}

// Bit 0 = Sunday. Confirmed against utils/userUtils/compute/computeWeekly.js,
// which tests `weekDaysBitmap & (1 << weekday)` with moment's day() (0 = Sun).
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function formatWeekDays(bitmap) {
  if (bitmap === null || bitmap === undefined) return null;
  const days = DAY_NAMES.filter((_, i) => bitmap & (1 << i));
  if (!days.length) return null;
  if (days.length === 7) return "Every day";
  return days.join(", ");
}

/**
 * Just the recurrence — deliberately WITHOUT the time and calendar type.
 *
 * formatWhen() packs all three into one string because a table cell has room
 * for only one column. The detail view has its own Time and Calendar fields, so
 * reusing formatWhen there printed the time twice and the calendar twice
 * ("Weekly · at 12:51 · (hebrew)" next to "12:51" and "hebrew").
 */
export function formatRecurrence(schedule) {
  const when = schedule?.when || {};
  const type = when.scheduleType || schedule?.scheduleType;
  if (!type) return "—";

  const label = type.charAt(0).toUpperCase() + type.slice(1);

  if (type === "weekly") {
    const days = formatWeekDays(when.weekDaysBitmap ?? schedule?.weekDaysBitmap);
    return days ? `${label} · ${days}` : label;
  }
  return label;
}

/** Just the clock time. Splitting formatDateTime()'s output on " · " worked
 *  only as long as that separator never changed — this does not. */
export function formatTime(value) {
  if (!value) return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("h:mm A") : "";
}
